import React, { useContext, useEffect, useState } from "react";
import { Card, Tag, Tabs, Spin, Empty, Typography, Badge, Table } from "antd";
import { AndroidOutlined, AppleOutlined, LinkOutlined, ClockCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import moment from "moment";

const { Text } = Typography;

const labelStyle = {
    fontSize: 12,
    color: "var(--color-text-secondary, #888)",
    marginBottom: 2,
    display: "block",
};

const valueStyle = {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-text-primary, #222)",
    wordBreak: "break-all",
};

function VersionCard({ data, platform }) {
    if (!data || !Array.isArray(data) || data.length === 0) {
        return <Empty description="No data available" style={{ padding: "32px 0" }} />;
    }

    const columns = [
        {
            title: "Version",
            dataIndex: `${platform}_latest_version`,
            key: "version",
            render: (text) => <Tag color="blue" style={{ fontWeight: "bold" }}>v{text}</Tag>,
            width: 100,
        },
        {
            title: "Force Version",
            dataIndex: `${platform}_force_update_version`,
            key: "force_version",
            render: (text) => text || "—",
            width: 120,
        },
        {
            title: "Type",
            dataIndex: `${platform}_update_type`,
            key: "type",
            render: (type) => {
                if (!type) return "—";
                const color = type === "immediate" ? "error" : "processing";
                const label = type === "immediate" ? "Immediate" : "Flexible";
                return <Badge status={color} text={label} />;
            },
            width: 120,
        },
        {
            title: "Remarks",
            key: "remarks",
            render: (_, record) => {
                const remark = record[`${platform}_version_remarks`] || record[`${platform}_remarks`];
                return <Text type="secondary" style={{ fontSize: 13 }}>{remark || "—"}</Text>;
            },
        },
        {
            title: "Store URL",
            dataIndex: platform === "ios" ? "store_url_ios" : "store_url_android",
            key: "store_url",
            render: (url) => url ? (
                <a href={url} target="_blank" rel="noreferrer">
                    <LinkOutlined /> Link
                </a>
            ) : "—",
            width: 100,
        },
        {
            title: "Date",
            dataIndex: "created_at",
            key: "date",
            render: (date) => (
                <div style={{ fontSize: 12, color: "#888" }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {moment(date).format("DD MMM YYYY, hh:mm A")}
                </div>
            ),
            width: 180,
        },
    ];

    return (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden" }}>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="middle"
                scroll={{ x: 600 }}
            />
        </div>
    );
}

export default function ViewAppVersions() {
    const { authData, backUrl } = useContext(AuthContext);

    const [androidData, setAndroidData] = useState([]);
    const [iosData, setIosData] = useState([]);
    const [loading, setLoading] = useState(true);

    const authHeader = {
        Authorization: `Bearer ${authData?.access}`,
    };

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [androidRes, iosRes] = await Promise.all([
                    axios.get(`${backUrl}/api/emp/app-update-history/?platform=android`, {
                        headers: authHeader,
                    }),
                    axios.get(`${backUrl}/api/emp/app-update-history/?platform=ios`, {
                        headers: authHeader,
                    }),
                ]);

                setAndroidData(androidRes.data || []);
                setIosData(iosRes.data || []);
            } catch (err) {
                console.error("Error fetching app versions:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    const getLatestVersion = (data, platform) => {
        if (!Array.isArray(data) || !data[0]) return null;
        return data[0][`${platform}_latest_version`];
    };

    const tabItems = [
        {
            key: "android",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AndroidOutlined style={{ color: "#3ddc84" }} />
                    Android
                    {getLatestVersion(androidData, "android") && (
                        <Tag color="success" style={{ fontSize: 11 }}>
                            v{getLatestVersion(androidData, "android")}
                        </Tag>
                    )}
                </span>
            ),
            children: <VersionCard data={androidData} platform="android" />,
        },
        {
            key: "ios",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AppleOutlined />
                    iOS
                    {getLatestVersion(iosData, "ios") && (
                        <Tag color="default" style={{ fontSize: 11 }}>
                            v{getLatestVersion(iosData, "ios")}
                        </Tag>
                    )}
                </span>
            ),
            children: <VersionCard data={iosData} platform="ios" />,
        },
    ];

    return (
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: 24 }}>
            <Card title="App Version Status">
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Tabs defaultActiveKey="android" items={tabItems} />
                )}
            </Card>
        </div>
    );
}